import { useCallback } from 'react';
import { contactViaWhatsApp } from '../utils/whatsappUtils';

/**
 * Custom hook for WhatsApp contact functionality
 * Makes it easy to implement WhatsApp contact across the site
 * 
 * @returns {Object} WhatsApp contact utilities
 */
const useWhatsAppContact = () => {
  /**
   * Creates a product object for WhatsApp contact from diamond details
   * 
   * @param {Object} diamond - Diamond details
   * @param {number|string} diamond.carat - Diamond carat
   * @param {string} diamond.clarity - Diamond clarity
   * @param {string} diamond.color - Diamond color
   * @param {string} diamond.cut - Diamond cut
   * @param {string} [diamond.shape="round"] - Diamond shape
   * @param {number|string} [diamond.price] - Diamond price
   * @param {string} [diamond.name] - Diamond name
   * @param {string} [diamond.image] - Diamond image URL
   * @param {string} [diamond.description] - Diamond description
   * @returns {Object} Product object for WhatsApp contact
   */
  const createDiamondProduct = useCallback((diamond) => {
    const { carat, clarity, color, cut, shape = 'round', price, name, image, description } = diamond;
    
    // Create a default name if not provided
    const productName = name || `Diamond (${carat} carat, ${clarity} clarity, ${color} color, ${cut} cut)`;
    
    // Format price if it's a number
    const formattedPrice = typeof price === 'number' 
      ? `₹${price.toLocaleString()}` 
      : price;
    
    return {
      name: productName,
      price: formattedPrice,
      image: image || '/media/diamond-placeholder.jpg',
      description: description || `${productName} with ${carat} carat, ${clarity} clarity, ${color} color, and ${cut} cut.`,
      specifications: {
        carat,
        clarity,
        color,
        cut,
        shape
      }
    };
  }, []);
  
  /**
   * Contacts via WhatsApp with diamond details
   * 
   * @param {Object} diamond - Diamond details
   */
  const contactWithDiamond = useCallback((diamond) => {
    const product = createDiamondProduct(diamond);
    contactViaWhatsApp(product);
  }, [createDiamondProduct]);
  
  return {
    contactViaWhatsApp,
    contactWithDiamond,
    createDiamondProduct
  };
};

export default useWhatsAppContact; 